/**
 * BrandDetailContainer
 * --
 */
import React from 'react';
import BrandDetailPresenter from './BrandDetailPresenter';
import { useParams } from 'react-router-dom';
import { decrypt } from 'utils/Crypto';
import { useCreateCart, useUpdateCart } from 'hooks/CartHooks';
import { useCreateWishlist, useDeleteWishlist } from 'hooks/WishlistHooks';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import MessageAlert, {
  extractApplicationPolicy,
  flattenTreeData,
  STORE_URL,
} from 'utils';
import lodash from 'utils/Lodash';

const TAP = [
  {
    key: 'DESC-comment',
    label: '판매인기순',
  },
  {
    key: 'ASC-finalPrice',
    label: '낮은가격순',
  },
  {
    key: 'DESC-finalPrice',
    label: '높은가격순',
  },
  {
    key: 'DESC-score',
    label: '상품평순',
  },
  {
    key: 'DESC-create',
    label: '등록일순',
  },
];

const BrandDetailContainer = (props) => {
  /* ===== ROUTE ===== */
  const { brandId } = useParams();

  // 브랜드 idx
  const BrandIdx = React.useMemo(() => {
    const decodeIdx = decrypt(decodeURIComponent(brandId));
    return Number(decodeIdx);
  }, [brandId]);

  /* ===== PROPS ===== */
  const { navigate, location, searchParams, screenSize } = props;
  const select = searchParams.get('category');

  /* ===== Mutate ===== */
  const { createCart } = useCreateCart();
  const { updateCart } = useUpdateCart();
  const { createWishlist } = useCreateWishlist();
  const { deleteWishlist } = useDeleteWishlist();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const { cartList, wishlist } = useUserDataStack();
  const userDataAction = useUserDataAction();
  const { shops, products, categories, policies, promotion, settings } =
    useDefaultShopDataStack();

  /* ===== STATE ===== */
  // 선택 카테고리
  const [selectCategory, setSelectCategory] = React.useState(categories[0]);
  // Modal Data
  const [modalData, setModalData] = React.useState({
    type: '',
    data: null,
  });

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 장바구니/위시리스트 추가
   * --
   * @param {string} t
   * @returns
   */
  const handleSubmit = async (t = 'cart', d) => {
    let mutateData = {};
    switch (t) {
      case 'cart':
        mutateData = {
          user_id: userInfo.user_id,
          product_id: d.key,
          cart_num: d.cartNum,
          shop_information_id: d.shopKey,
          product_option: d.product_option ?? null,
          cart_type: d.type,
          regular_cycle: d.regular_cycle,
        };

        const filterSameProduct = cartList.filter(
          (cart) => cart.product_id === d.key
        );

        // NOTE: 장바구니에 같은 상품이 없을 경우 Create
        if (!filterSameProduct.length) {
          await handleMutate(createCart, mutateData).then((res) => {
            if (res.status !== 200) {
              MessageAlert.warning('장바구니 추가에 실패하였습니다.');
              return;
            }
            MessageAlert.success('장바구니에 추가되었습니다.');
            userDataAction.addData('cartList', res.data);
          });
        }
        // NOTE: 장바구니에 같은 상품이 있을 경우
        else {
          const isSame = cartList.find((cart) => {
            const {
              cart_num: cartListNum,
              is_fixed,
              created_at,
              updated_at,
              cart_id,
              ...cartRest
            } = cart;
            const { cart_num: newValueNum, ...newValueRest } = mutateData;

            return lodash.isObjEqual(cartRest, newValueRest);
          });

          // NOTE: 같은 옵션의 상품이 있을 경우 수량 업데이트
          if (isSame) {
            mutateData = {
              ...isSame,
              cart_num: isSame.cart_num + d.cartNum,
            };
            await handleMutate(updateCart, mutateData).then((res) => {
              if (res.status !== 200) {
                MessageAlert.warning('장바구니 추가에 실패하였습니다.');
                return;
              }
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.updateData('cartList', isSame, res.data);
            });
          }
          // NOTE: 같은 옵션의 상품이 아닐 경우 장바구니 추가
          else {
            await handleMutate(createCart, mutateData).then((res) => {
              if (res.status !== 200) {
                MessageAlert.warning('장바구니 추가에 실패하였습니다.');
                return;
              }
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.addData('cartList', res.data);
            });
          }
        }

        return;
      case 'wishlist':
        mutateData = {
          user_id: userInfo.user_id,
          product_id: d.key,
        };
        await handleMutate(createWishlist, mutateData).then((res) => {
          if (res.status !== 200) {
            MessageAlert.warning('찜목록에 추가에 실패하였습니다.');
            return;
          }
          MessageAlert.success('찜목록에 추가되었습니다.');
          userDataAction.addData('wishlist', res.data);
        });
        return;
      case 'remove':
        mutateData = wishlist.find((wish) => wish.product_id === d.key);
        await handleMutate(deleteWishlist, mutateData.wishlist_id).then(
          (res) => {
            if (res.status !== 200) {
              MessageAlert.warning('찜목록에서 삭제하는데 실패하였습니다.');
              return;
            }
            MessageAlert.success('찜목록에서 삭제되었습니다.');
            userDataAction.removeData('wishlist', 'wishlist_id', mutateData);
          }
        );
        return;
      default:
        MessageAlert.warning(
          `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
        );
        return;
    }
  };

  /**
   * Mutate Function 실행
   * --
   * @param {function} mutateFn
   * @param {object} mutate
   * @returns
   */
  const handleMutate = async (mutateFn, mutate) => {
    try {
      const res = await mutateFn(mutate);

      setModalData({ type: '', data: null });
      return res;
    } catch (err) {
      console.error('[Product handleMutate] Error !! ', err);
      // MessageAlert.error(
      //   `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
      // );
      throw err;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 카테고리 플랫 정제
   * --
   */
  const FlattenCategories = React.useMemo(() => {
    // 카테고리 플랫형태 정제
    return flattenTreeData(categories);
  }, [categories]);

  /**
   * 카테고리 정제
   * --
   */
  const CategoryList = React.useMemo(() => {
    const mapping = (cate) => {
      const { key, product_category_name, children = [] } = cate;

      return {
        key: key,
        title: product_category_name,
        children: children.map((child) => mapping(child)),
      };
    };

    // 브랜드에서 사용하는 상품 추출
    const shopProduct = products.filter(
      (product) => product.shop_information_id === BrandIdx
    );

    // 브랜드에서 사용하는 카테고리만 추출
    const filterCategories = FlattenCategories.filter((category) =>
      shopProduct.some(
        (product) =>
          product.product_category_id === category.product_category_id
      )
    );

    // 사용하는 카테고리 키 추출
    const useCategory = filterCategories.map((category) => category.key);

    // 카테고리 중복값 제거
    const categoryIdxList = Array.from(
      useCategory.reduce((acc, category) => {
        const splitKey = category.split('-');

        splitKey.forEach((key) => acc.add(key));

        return acc;
      }, new Set())
    );

    // 브랜드에서 사용하고 있는 상위 카테고리값까지 추출
    const filterCategoryList = categories.filter((category) =>
      categoryIdxList.some(
        (idx) => Number(idx) === category.product_category_id
      )
    );

    // 카테고리 트리형식 정제
    const newCategories = filterCategoryList.map((cate) => mapping(cate));

    return newCategories ?? [];
  }, [FlattenCategories, products, BrandIdx, categories]);

  /**
   * 브랜드 데이터 정제
   * --
   */
  const Brand = React.useMemo(() => {
    const findShop = shops.find(
      (shop) => shop.shop_information_id === BrandIdx
    );

    const backgroundImage = findShop?.shop_information_file
      ? findShop.shop_information_file.find((file) => file.type === 'STORE')
      : null;

    return {
      ...findShop,
      background: backgroundImage
        ? `${STORE_URL}/${backgroundImage.file_url}`
        : null,
    };
  }, [shops, BrandIdx]);

  /**
   * 상품 목록 정제
   * --
   */
  const ProductList = React.useMemo(() => {
    // 쇼핑몰 추출
    const shopData = shops.find(
      (shop) => shop.shop_information_id === BrandIdx
    );

    const newProducts = products
      ?.filter((product) => product.shop_information_id === BrandIdx)
      .map((product) => {
        const {
          product_file,
          product_name,
          num,
          price,
          product_status,
          product_category_id,
          product_id,
          shop_information_id,
          created_at,
          average_score,
          review_count,
          tag,
          product_option,
          packaging_method,
          type: delivery_type,
        } = product;

        // 썸네일 추출
        const thumbnail =
          product_file.find((file) => file.type === 'THUMBNAIL') || null;

        // 태그 추출
        const tagList = tag.map((tag) => ({
          id: tag.tag_id,
          label: tag.tag_name,
        }));

        // 적용 정책 추출
        const applyPolicy = policies.filter((policy) =>
          policy.map_product_policy.some((map) => map.product_id === product_id)
        );
        // 우선순위에 따른 적용되는 정책 추출
        const applicationPolicy = applyPolicy.length
          ? extractApplicationPolicy(applyPolicy, price, promotion)
          : null;

        // 카테고리 key 추출
        const categoryKey =
          FlattenCategories.find(
            (category) => category.product_category_id === product_category_id
          ) ?? '';

        return {
          key: product_id,
          category: categoryKey.key,
          title: product_name,
          price: price,
          isSoldOut: num === 0 || product_status === 'SOLDOUT' ? 'Y' : 'N',
          src: !thumbnail ? null : `${STORE_URL}/${thumbnail.file_url}`,
          tags: tagList,
          brand: shopData ? shopData.shop_information_name : '',
          shopKey: shop_information_id,
          discount: applicationPolicy
            ? applicationPolicy.apply_discount_rate
            : 0,
          discount_type: applicationPolicy
            ? applicationPolicy.discount_type
            : 'W',
          finalPrice: applicationPolicy
            ? price - applicationPolicy.discount_price
            : price,
          isWish: wishlist.some((wish) => wish.product_id === product_id),
          create: created_at,
          score: average_score,
          comment: review_count,
          sales: 0, // TODO: 판매순
          options: product_option,
          type: delivery_type,
          policy: applicationPolicy,
          packaging: packaging_method,
        };
      });

    return newProducts;
  }, [
    products,
    shops,
    policies,
    FlattenCategories,
    wishlist,
    promotion,
    BrandIdx,
  ]);

  /**
   * 장바구니 상품수
   * --
   */
  const CartNum = React.useMemo(() => {
    return cartList.length
      ? cartList.reduce((acc, cur) => {
          return (acc += cur.cart_num);
        }, 0)
      : 0;
  }, [cartList]);

  /**
   * 필터 아이템 정제
   * --
   */
  const FilterItems = React.useMemo(() => {
    return [
      {
        title: '배송타입',
        options: [
          { label: '택배배송', value: 'DELIVERY' },
          { label: '정기배송', value: 'SUBSCRIPTION' },
        ],
        multiple: true,
        key: 'type',
        type: 'include',
      },
      {
        title: '포장타입',
        options: [
          { label: '일반', value: 'GENERAL' },
          { label: '냉장', value: 'REFRIGERATION' },
          { label: '냉동', value: 'FREEZING' },
        ],
        key: 'packaging',
        type: 'include',
      },
      {
        title: '가격',
        options: [
          { label: '10,000원 미만', value: [0, 10000] },
          { label: '10,000원 ~ 50,000원', value: [10000, 50000] },
          { label: '50,000원 ~ 100,000원', value: [50000, 100000] },
          { label: '100,000원 이상', value: [100000, 9999999999] },
        ],
        key: 'finalPrice',
        type: 'range',
      },
    ];
  }, []);

  /**
   * 정렬 아이템 정제
   * --
   */
  const SortItems = React.useMemo(() => {
    if (!settings) return TAP;

    const { product_standard, product_order_by } = settings;

    let standard = '';
    switch (product_standard) {
      case 'NAME':
        standard = `${product_order_by}-title`;
        break;
      case 'PRICE':
        standard = `${product_order_by}-price`;
        break;
      case 'REGISTER':
        standard = `${product_order_by}-create`;
        break;
      default:
        standard = `${product_order_by}-finalPrice`;
        break;
    }

    return [
      {
        key: standard,
        label: '추천순',
      },
      ...TAP,
    ];
  }, [settings]);

  /**
   * Query Key 카테고리 정제
   * --
   */
  React.useEffect(() => {
    const decodeKey = decrypt(decodeURIComponent(select));

    const findCategory = FlattenCategories.find(
      (category) => category.product_category_id === Number(decodeKey)
    );

    setSelectCategory(findCategory);
  }, [select, FlattenCategories]);

  /* ===== RENDER ===== */
  return (
    <>
      <BrandDetailPresenter
        navigate={navigate}
        location={location}
        brand={Brand}
        products={ProductList}
        categories={CategoryList}
        selectedCategory={selectCategory}
        filterItems={FilterItems}
        modalData={modalData}
        setModalData={setModalData}
        user={userInfo}
        cartList={CartNum}
        onSubmit={handleSubmit}
        sortItems={SortItems}
        screenSize={screenSize}
      />
    </>
  );
};

export default BrandDetailContainer;
